import { Search } from "@mui/icons-material";
import {
  FormControl,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import RandomBubble from "./RandomBubble";
import { useConfig } from "../containers/ConfigContext";
import { getSentimentKeywordGroupConfig, getSentimentKeywords } from "../actions/sentiment.action";
import { getOptions } from "../models/report.model";
import { bubbleDataMap, SentimentConfig, GetSentimentParam, SentimentKeyword } from "../models/sentiment.model";
import TextAnalysisSentimentsList from "./TextAnalysisSentimentsList";
import { getUrlParam } from "../lib/helper";
import { theme } from "../lib/theme";
import { debounce } from "lodash";

export default function TextAnalysisBubbleDetails() {
  const mobile = useMediaQuery(theme.breakpoints.down("md")) || !!getUrlParam("mobile");
  const { filter, loggedUser } = useConfig();
  const [search, setSearch] = useState("");
  const [keywordGroupConfig, setKeywordGroupConfig] = useState<SentimentConfig>();

  useEffect(() => {
    getSentimentKeywordGroupConfig().then((res) => {
      setKeywordGroupConfig(res);
    });
  }, []);

  const filterParams = useMemo<GetSentimentParam>(() => {
    const { hierarchyFilter, ...rest } = filter || {};

    const params = {
      id: hierarchyFilter || getOptions(loggedUser)?.[0]?.value,
      search,
      ...rest,
    };

    return params;
  }, [filter, loggedUser, search])

  const { keywords, loading: loadingKeywords } = useSentimentKeywords(filterParams);

  return (
    <div>
      <div className="padding-b-big padding-l-big">
        <FormControl variant="standard">
          <InputLabel>Search</InputLabel>
          <Input
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <Search />
                </IconButton>
              </InputAdornment>
            }
            onChange={debounce((e) => {
              setSearch(e.target.value);
            }, 500)}
          />
        </FormControl>
      </div>
      <Paper className={mobile ? "" : "padding-normal"} elevation={mobile ? 0 : 2}>
        <Stack direction="column">
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h6">TA Explore</Typography>
            <div></div>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack direction="column" spacing={2} className="height-100p">
                <RandomBubble
                  loading={loadingKeywords}
                  config={keywordGroupConfig}
                  data={keywords.map((k, i) => ({
                    keyword: k,
                    position: bubbleDataMap[i] || {
                      width: 3,
                      left: 0,
                      top: 0,
                    },
                  }))}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextAnalysisSentimentsList
                mobile={mobile}
                filterParams={filterParams}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>
    </div>
  );
}

function useSentimentKeywords(filter: GetSentimentParam) {
  const [keywords, setKeywords] = useState<SentimentKeyword[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filter.id) return;
    let ignore = false;

    setLoading(true);
    getSentimentKeywords(filter).then(res => {
      if (!ignore) {
        setKeywords(res);
      }
    }).finally(() => {
      setLoading(false);
      if (!ignore) {
        setLoading(false);
      }
    });

    return () => {
      ignore = true;
    };
  }, [filter])

  return { keywords, loading };
}
